import * as React from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  DropdownMenu,
  EllipsisVerticalIcon,
  IconButton,
} from '@buffer-mono/popcorn'
import type { DropdownMenuGroupProps } from '@buffer-mono/popcorn'

import styles from './MoreActionsDropdown.module.css'

const isChildrenEmpty = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).length === 0

const MoreActionsDropdownGroup = ({
  children,
  className,
  ...props
}: DropdownMenuGroupProps): JSX.Element | null => {
  if (isChildrenEmpty(children)) {
    return null
  }

  return (
    <>
      <DropdownMenu.Group {...props} className={clsx(styles.group, className)}>
        {children}
      </DropdownMenu.Group>
      <DropdownMenu.Separator className={styles.separator} />
    </>
  )
}

const hasActionChildren = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return true
    if (child.type !== MoreActionsDropdownGroup) return true
    return !isChildrenEmpty(child.props.children)
  })

const MoreActionsDropdown = ({
  children,
  triggerRef,
  label = 'More actions',
}: {
  children: React.ReactNode
  /**
   * Ref to the "More actions" trigger button. Dialogs opened from menu items
   * restore focus here on close — the menu item that opened them unmounts
   * with the menu, so this button is the nearest persistent control.
   */
  triggerRef?: React.Ref<HTMLButtonElement>
  label?: string
}): JSX.Element | null => {
  if (!hasActionChildren(children)) {
    return null
  }

  return (
    <DropdownMenu
      modal={false}
      trigger={
        <IconButton
          ref={triggerRef}
          data-testid="more-actions-menu"
          label={label}
          tooltip="More actions"
        >
          <EllipsisVerticalIcon />
        </IconButton>
      }
    >
      {children}
    </DropdownMenu>
  )
}

const MoreActionsDropdownItem: typeof DropdownMenu.Item = DropdownMenu.Item

type MoreActionsDropdownComponent = typeof MoreActionsDropdown & {
  Group: typeof MoreActionsDropdownGroup
  Item: typeof MoreActionsDropdownItem
}

const MoreActionsDropdownObject: MoreActionsDropdownComponent = Object.assign(
  MoreActionsDropdown,
  {
    Group: MoreActionsDropdownGroup,
    Item: MoreActionsDropdownItem,
  },
)

export { MoreActionsDropdownObject as MoreActionsDropdown }
